import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { suppressGlobalErrorNavigation } from '../../core/interceptors/error/suppress-global-error';
import { ConfigService } from '../../core/services/config/config.service';
import { UtilityService } from '../../core/services/utility/utility.service';
import {
  PatientPortalAccess,
  PatientPortalAccessBlockReasonType,
  PatientPortalAccessBlockRequest,
  PatientPortalAccessRestoreRequest,
  PatientPortalAccessRestoreResult,
} from '../models/patient-portal-access';

/**
 * A patient's portal access — the read behind the Access & Controls detail screen and the writes
 * that change it.
 *
 * Separate from `PatientAccessSearchService`, which answers "who is this patient": that one reads
 * the portal-owned patient table and is shared with the search dialog and results table. This one
 * owns `/Patients/{patientId}/PortalAccess` and its sub-resources — the read, the block, the
 * restore and the proxy revoke — which are access-control operations on a patient already in
 * view.
 *
 * Every call opts out of the global error navigation. The detail screen distinguishes its failure
 * states in place — "not found" from "couldn't load", a refused block from a broken one — and
 * `ErrorInterceptor`'s default hop to `/error` would tear the component down before any of them
 * could render. A 401 still reaches the sign-in flow; the interceptor makes that non-suppressible.
 */
@Injectable({
  providedIn: 'root',
})
export class PatientPortalAccessService {
  private readonly utilityService = inject(UtilityService);
  private readonly configService = inject(ConfigService);

  /**
   * Read per call rather than captured at construction: an eager field makes merely *creating*
   * the service throw `ConfigNotLoadedError` when config is unavailable, which reaches callers
   * that never intended to use it.
   */
  private get apiBaseUrl(): string {
    return this.configService.config.adminDashboardApiUrl;
  }

  private portalAccessEndpoint(patientId: number): string {
    return `${this.apiBaseUrl}/Patients/${patientId}/PortalAccess`;
  }

  /**
   * The patient's portal access: whether a staff block is in force, the block on file, and every
   * portal account linked to the patient — their own (Self) account and revoked links included.
   *
   * `GET /Patients/{patientId}` answers identity only, so this is a second call rather than a
   * richer first one.
   */
  getPortalAccess(patientId: number): Observable<PatientPortalAccess> {
    return this.utilityService.getRequest<PatientPortalAccess>(
      this.portalAccessEndpoint(patientId),
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * The options for the Block Reason dropdown, and the ids {@link blockPortalAccess} is keyed on.
   *
   * Fetched rather than written out in the UI: the block request takes an id, and the label staff
   * read is a database column. See {@link PatientPortalAccessBlockReasonType}.
   */
  getBlockReasonTypes(): Observable<PatientPortalAccessBlockReasonType[]> {
    return this.utilityService.getRequest<PatientPortalAccessBlockReasonType[]>(
      `${this.apiBaseUrl}/PatientPortalAccessBlockReasonTypes`,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * Blocks the patient out of the portal — their own account and every proxy's — and records who
   * did it, when and why.
   *
   * A `PUT`, not a `POST`: blocking sets `pat.Patient.IsPortalAccessBlocked` and the endpoint
   * refuses a repeat (`400`, "already blocked") rather than stacking a second block.
   *
   * It answers the patient's access state **after** the block, so the caller replaces what it
   * holds with the response rather than patching its own copy — the response carries the new
   * history row's id, timestamp and acting admin, none of which the caller can know.
   */
  blockPortalAccess(
    patientId: number,
    request: PatientPortalAccessBlockRequest,
  ): Observable<PatientPortalAccess> {
    return this.utilityService.putRequest<PatientPortalAccess>(
      `${this.portalAccessEndpoint(patientId)}/Block`,
      request,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * Lifts the block, letting the patient and every proxy whose link is still active back in.
   * Proxy links that were **revoked** stay revoked — a restore undoes the block and nothing else.
   *
   * Like the block it is a `PUT` and refuses a no-op: restoring a patient who isn't blocked is a
   * `400`, not a success.
   *
   * It answers more than the new access state. The server also tries to tell the patient, after
   * the commit and best-effort, and reports whether that happened — see
   * {@link PatientPortalAccessRestoreResult.isPatientNotified}. The caller must branch its "the
   * patient has been notified" wording on that flag rather than on the call having succeeded.
   */
  restorePortalAccess(
    patientId: number,
    request: PatientPortalAccessRestoreRequest,
  ): Observable<PatientPortalAccessRestoreResult> {
    return this.utilityService.putRequest<PatientPortalAccessRestoreResult>(
      `${this.portalAccessEndpoint(patientId)}/Restore`,
      request,
      false,
      suppressGlobalErrorNavigation(),
    );
  }

  /**
   * Permanently withdraws one proxy's access to this patient, by the `UserPatient` **link** id —
   * not the portal user's id. The link moves Active → Revoked.
   *
   * Two things make this the heaviest write on the screen, and callers must treat it as such:
   *
   * 1. **Revoked is terminal.** Lifting a later block does not bring it back; re-granting takes a
   *    new proxy verification request. There is no undo endpoint.
   * 2. **It can take the proxy's whole portal account with it.** When this was the user's last
   *    active link, the API disables their Entra identity and moves `pat.User` to Revoked in the
   *    same transaction — so revoking a caregiver here can end their ability to sign in at all,
   *    including for other patients they may later be linked to.
   *
   * No request body: the link id in the path is the whole instruction.
   *
   * It answers the patient's access state after the revoke. Note the API reports whether the
   * account was also disabled only in the envelope's `messages`, which `putRequest` discards on
   * the way to `result` — so the caller cannot tell from the response, and must not claim either
   * way.
   */
  revokeProxyAccess(patientId: number, userPatientId: number): Observable<PatientPortalAccess> {
    return this.utilityService.putRequest<PatientPortalAccess>(
      `${this.portalAccessEndpoint(patientId)}/LinkedUsers/${userPatientId}/Revoke`,
      null,
      false,
      suppressGlobalErrorNavigation(),
    );
  }
}
